import { createContext } from 'react';
import type { EmgAuthClaims, EmgAuthClient } from './authClient';

/**
 * EAA-1825: `session-error` is a fourth, TERMINAL state. It means "we hold a
 * valid OIDC session but signing in again cannot fix what is failing" — the
 * portal has redirected to the IdP as often as a converging flow ever needs to
 * and is still being refused, so it stops and shows the failure instead of
 * reloading the tab forever (see `signInLoop.ts` for the mechanism).
 */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'session-error';

export interface AuthContextValue {
    /** Raw auth-client instance — used by `/callback` to run the code exchange. */
    client: EmgAuthClient;
    status: AuthStatus;
    authenticated: boolean;
    claims: EmgAuthClaims | null;
    accessToken: string | null;
    /**
     * EAA-1825: the ONLY way a screen should react to a 401. Never call
     * `client.login()` from a 401 handler — that is the unbounded reload loop
     * this replaces. This is loop-guarded: it redirects while the round trip is
     * still plausibly converging, and flips `status` to `session-error`
     * (rendering the failure) once it provably is not.
     */
    reauthenticate: (returnTo?: string) => void;
    logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
